/**
 * BrokerImportModal Component
 *
 * Modal for importing brokers from the broker catalog.
 * Provides search, filtering, and duplicate handling.
 *
 * Pattern follows InstrumentImportModal for structure and BrokerModal for service integration.
 */

// 1. External dependencies
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Search, Package, AlertTriangle } from 'lucide-react';

// 2. Workspace packages
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Listbox,
  ListboxItem,
  ListboxSection,
  Chip,
  Switch,
  Alert,
  Accordion,
  AccordionItem,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Internal absolute imports
import { BROKER_CATALOG_DATA } from '@/data/brokerCatalog';
import { getBrokerFeeDetails } from '@/data/brokerFeeCatalog';
import type { BrokerCatalogEntry } from '@/types/reference/BrokerCatalog';
import type { BrokerFeeConfiguration as CatalogBrokerFeeConfiguration } from '@/types/reference/BrokerFeeCatalog';
import type { Broker, BrokerFeeConfiguration as SchemaBrokerFeeConfiguration } from '@/types/schema/Broker';
import type { OvernightFinancingStructure, CurrencyConversionStructure } from '@/types/shared/feeStructures';
import type {
  OvernightBenchmark,
  OvernightFrequency,
  OvernightAssetClass,
  ConversionRateType,
} from '@/types/shared/FeeEnums';
import type { AssetClassType } from '@/types/shared/AssetClass';
import { ASSET_CLASS_TYPE_DISPLAY_MAP } from '@/constants/assetClass';
import { formatAssetClassTypeDisplay } from '@/utils/assetClass';
import { brokerService } from '@/services/BrokerService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for BrokerImportModal
 */
export interface BrokerImportModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Callback after successful import - receives new broker ID */
  onImportComplete: (brokerId: string) => void;
  /** Existing brokers for duplicate detection (all statuses including archived) */
  existingBrokers: Broker[];
  /** Loading state from BrokersContext */
  loading: boolean;
}

/**
 * Filter state for broker selection
 */
interface FilterState {
  searchTerm: string;
  accountTypeFilter: 'all' | 'personal' | 'prop' | 'paper';
  assetClassFilter: AssetClassType | 'all';
  popularOnly: boolean;
}

/**
 * Initial filter state
 */
const initialFilterState: FilterState = {
  searchTerm: '',
  accountTypeFilter: 'all',
  assetClassFilter: 'all',
  popularOnly: false,
};

// =============================================================================
// Filter Options
// =============================================================================

/**
 * Account type filter options
 */
const accountTypeFilterOptions = [
  { key: 'all', label: 'All Account Types' },
  { key: 'personal', label: 'Personal / General' },
  { key: 'prop', label: 'Prop Firm' },
  { key: 'paper', label: 'Paper Trading' },
] as const;

/**
 * Asset class filter options
 */
const assetClassFilterOptions = [
  { key: 'all', label: 'All Asset Classes' },
  { key: 'futures' as AssetClassType, label: formatAssetClassTypeDisplay('futures', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'forex' as AssetClassType, label: formatAssetClassTypeDisplay('forex', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'stocks' as AssetClassType, label: formatAssetClassTypeDisplay('stocks', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'crypto' as AssetClassType, label: formatAssetClassTypeDisplay('crypto', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'options' as AssetClassType, label: formatAssetClassTypeDisplay('options', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'cfd' as AssetClassType, label: formatAssetClassTypeDisplay('cfd', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'bonds' as AssetClassType, label: formatAssetClassTypeDisplay('bonds', ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'funds' as AssetClassType, label: formatAssetClassTypeDisplay('funds', ASSET_CLASS_TYPE_DISPLAY_MAP) },
] as const;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if a catalog entry matches the account type filter
 */
const matchesAccountType = (
  entry: BrokerCatalogEntry,
  filter: FilterState['accountTypeFilter']
): boolean => {
  if (filter === 'all') return true;

  if (filter === 'personal') {
    // Match array account types (general brokers) or explicit 'personal'
    return Array.isArray(entry.accountType) || entry.accountType === 'personal';
  }

  return entry.accountType === filter;
};

// =============================================================================
// Enum Normalization Helpers
// =============================================================================

/** Valid OvernightBenchmark values for validation */
const VALID_BENCHMARKS: readonly OvernightBenchmark[] = [
  'sofr', 'sonia', 'estr', 'tonar', 'tndr', 'libor', 'custom',
];

/** Valid OvernightFrequency values for validation */
const VALID_FREQUENCIES: readonly OvernightFrequency[] = [
  'daily', 'weekdayOnly', 'tripleWednesday', 'tripleFriday',
];

/** Valid OvernightAssetClass values for validation */
const VALID_OVERNIGHT_ASSET_CLASSES: readonly OvernightAssetClass[] = [
  'forex', 'indices', 'stocks', 'commodities', 'crypto',
];

/** Valid ConversionRateType values for validation */
const VALID_RATE_TYPES: readonly ConversionRateType[] = [
  'spot', 'spotPlusMarkup', 'fixed', 'undisclosed',
];

/**
 * Normalize benchmark value from catalog to schema enum
 * Catalog uses uppercase (e.g., "SOFR"), schema expects lowercase ('sofr')
 */
const normalizeBenchmark = (value: string | undefined): OvernightBenchmark => {
  if (!value) return 'custom';
  const lowercased = value.toLowerCase();
  if (VALID_BENCHMARKS.includes(lowercased as OvernightBenchmark)) {
    return lowercased as OvernightBenchmark;
  }
  return 'custom';
};

/**
 * Normalize rate type from catalog to schema enum
 * Maps 'interbank' → 'spot' (same meaning)
 */
const normalizeRateType = (value: string | undefined): ConversionRateType => {
  if (!value) return 'spotPlusMarkup';
  // Map catalog-only value to schema equivalent
  if (value === 'interbank') return 'spot';
  if (VALID_RATE_TYPES.includes(value as ConversionRateType)) {
    return value as ConversionRateType;
  }
  return 'spotPlusMarkup';
};

/**
 * Normalize overnight asset class from catalog to schema enum
 * Validates against schema values, defaults to 'forex' (safest general default)
 */
const normalizeOvernightAssetClass = (value: string | undefined): OvernightAssetClass => {
  if (!value) return 'forex';
  if (VALID_OVERNIGHT_ASSET_CLASSES.includes(value as OvernightAssetClass)) {
    return value as OvernightAssetClass;
  }
  // Note: 'forex' is safest general default; futures-heavy brokers may differ
  return 'forex';
};

/**
 * Normalize overnight frequency from catalog to schema enum
 * Maps all invalid values to 'daily'
 */
const normalizeOvernightFrequency = (value: string | undefined): OvernightFrequency => {
  if (!value) return 'daily';
  if (VALID_FREQUENCIES.includes(value as OvernightFrequency)) {
    return value as OvernightFrequency;
  }
  return 'daily';
};

// =============================================================================
// Fee Configuration Transformation Helpers
// =============================================================================

/**
 * Transform catalog overnight financing to schema overnight financing
 * Sets required defaults for fields that are optional in catalog but required in schema
 */
const transformOvernightFinancing = (
  catalog: NonNullable<CatalogBrokerFeeConfiguration['overnightFinancing']>
): OvernightFinancingStructure => {
  const result: OvernightFinancingStructure = {
    benchmark: normalizeBenchmark(catalog.benchmark),
    adminSpread: catalog.adminSpread,
    adminSpreadPrecision: catalog.adminSpreadPrecision,
    frequency: normalizeOvernightFrequency(catalog.frequency),
    assetClass: normalizeOvernightAssetClass(catalog.assetClass),
    hasTripleCharges: catalog.hasTripleCharges ?? false,
    verificationStatus: 'template',
  };
  // Only include optional properties if they have values
  if (catalog.tripleChargeDay !== undefined) {
    result.tripleChargeDay = catalog.tripleChargeDay as NonNullable<OvernightFinancingStructure['tripleChargeDay']>;
  }
  if (catalog.verificationNotes) {
    result.verificationNotes = catalog.verificationNotes;
  }
  return result;
};

/**
 * Transform catalog currency conversion to schema currency conversion
 * Sets required defaults for fields that are optional in catalog but required in schema
 */
const transformCurrencyConversion = (
  catalog: NonNullable<CatalogBrokerFeeConfiguration['currencyConversion']>
): CurrencyConversionStructure => {
  const result: CurrencyConversionStructure = {
    rateType: normalizeRateType(catalog.rateType),
    triggers: (catalog.triggers ?? []) as CurrencyConversionStructure['triggers'],
    usesRealTimeRates: catalog.usesRealTimeRates ?? true,
    verificationStatus: 'template',
  };
  // Only include optional properties if they have values
  if (catalog.markup !== undefined) {
    result.markup = catalog.markup;
  }
  if (catalog.markupPrecision !== undefined) {
    result.markupPrecision = catalog.markupPrecision;
  }
  if (catalog.verificationNotes) {
    result.verificationNotes = catalog.verificationNotes;
  }
  return result;
};

/**
 * Transform catalog fee configuration to schema fee configuration
 * Maps catalog types to schema types with appropriate defaults
 */
const transformCatalogFeeConfiguration = (
  config: CatalogBrokerFeeConfiguration,
  feeDetailsLastUpdated: string
): SchemaBrokerFeeConfiguration => {
  const result: SchemaBrokerFeeConfiguration = {
    id: config.id,
    name: config.name,
    status: 'active',
    commission: config.commission,
    feesUpdatedAt: feeDetailsLastUpdated,
    // verification is intentionally omitted - catalog data hasn't been user-verified
  };
  // Only include optional properties if they have values
  if (config.instrumentId) {
    result.instrumentId = config.instrumentId;
  }
  if (config.category) {
    result.category = config.category;
  }
  if (config.appliesTo) {
    result.appliesTo = config.appliesTo;
  }
  if (config.overnightFinancing) {
    result.overnightFinancing = transformOvernightFinancing(config.overnightFinancing);
  }
  if (config.currencyConversion) {
    result.currencyConversion = transformCurrencyConversion(config.currencyConversion);
  }
  if (config.notes) {
    result.notes = config.notes;
  }
  return result;
};

// =============================================================================
// Component
// =============================================================================

/**
 * BrokerImportModal Component
 */
export const BrokerImportModal: React.FC<BrokerImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete,
  existingBrokers,
  loading,
}) => {
  // Toast notifications
  const { showToast } = useToastNotification();

  // Portal container ref for Select popovers (fixes aria-hidden warnings)
  const portalContainerRef = useRef<HTMLDivElement>(null);

  // ===========================================================================
  // State
  // ===========================================================================

  // Filter state
  const [filters, setFilters] = useState<FilterState>(initialFilterState);

  // Selection state (single selection mode)
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Duplicate confirmation state
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);

  // Error state
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Submitting state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ===========================================================================
  // Reset State on Modal Open
  // ===========================================================================

  useEffect(() => {
    if (isOpen) {
      setFilters(initialFilterState);
      setSelectedKey(null);
      setShowDuplicateConfirm(false);
      setSubmitError(null);
    }
  }, [isOpen]);

  // ===========================================================================
  // Computed Values
  // ===========================================================================

  // Set of existing broker reference IDs (includes all statuses)
  const existingBrokerReferenceIds = useMemo(() => {
    const ids = new Set<string>();
    existingBrokers.forEach((broker) => {
      if (broker.brokerReferenceId) {
        ids.add(broker.brokerReferenceId);
      }
    });
    return ids;
  }, [existingBrokers]);

  // Filtered catalog entries
  const filteredCatalog = useMemo(() => {
    return BROKER_CATALOG_DATA.filter((entry) => {
      // Search filter (name or shortName)
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const nameMatch = entry.name.toLowerCase().includes(searchLower);
        const shortNameMatch = entry.shortName?.toLowerCase().includes(searchLower);
        if (!nameMatch && !shortNameMatch) return false;
      }

      // Account type filter
      if (!matchesAccountType(entry, filters.accountTypeFilter)) {
        return false;
      }

      // Asset class filter
      if (filters.assetClassFilter !== 'all') {
        if (!entry.assetClasses.includes(filters.assetClassFilter)) {
          return false;
        }
      }

      // Popular only filter
      if (filters.popularOnly && !entry.isPopular) {
        return false;
      }

      return true;
    });
  }, [filters]);

  // Group filtered catalog by account type for sections
  const groupedCatalog = useMemo(() => {
    const groups = {
      personal: [] as BrokerCatalogEntry[],
      prop: [] as BrokerCatalogEntry[],
      paper: [] as BrokerCatalogEntry[],
    };

    filteredCatalog.forEach((entry) => {
      if (Array.isArray(entry.accountType) || entry.accountType === 'personal') {
        groups.personal.push(entry);
      } else if (entry.accountType === 'prop') {
        groups.prop.push(entry);
      } else if (entry.accountType === 'paper') {
        groups.paper.push(entry);
      }
    });

    // Sort each group by name
    Object.values(groups).forEach((group) => {
      group.sort((a, b) => a.name.localeCompare(b.name));
    });

    return groups;
  }, [filteredCatalog]);

  // Selected catalog entry
  const selectedCatalogEntry = useMemo(() => {
    if (!selectedKey) return null;
    return BROKER_CATALOG_DATA.find((entry) => entry.id === selectedKey) ?? null;
  }, [selectedKey]);

  // Existing brokers matching selected catalog entry
  const existingMatches = useMemo(() => {
    if (!selectedCatalogEntry) return [];
    return existingBrokers.filter(
      (broker) => broker.brokerReferenceId === selectedCatalogEntry.id
    );
  }, [selectedCatalogEntry, existingBrokers]);

  // Most recently updated existing match (for "Use Existing" action)
  // Falls back safely if updatedAt is missing (treat missing as oldest)
  const mostRecentExisting = useMemo(() => {
    if (existingMatches.length === 0) return null;
    if (existingMatches.length === 1) return existingMatches[0];

    return existingMatches.reduce((mostRecent, current) => {
      const mostRecentTime = mostRecent.updatedAt
        ? new Date(mostRecent.updatedAt).getTime()
        : 0;
      const currentTime = current.updatedAt
        ? new Date(current.updatedAt).getTime()
        : 0;
      return currentTime > mostRecentTime ? current : mostRecent;
    });
  }, [existingMatches]);

  // Whether a duplicate exists for the selected entry
  const duplicateExists = existingMatches.length > 0;

  // ===========================================================================
  // Handlers
  // ===========================================================================

  /**
   * Update filter state
   */
  const updateFilter = useCallback(
    <K extends keyof FilterState>(field: K, value: FilterState[K]): void => {
      setFilters((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  /**
   * Handle selection change
   */
  const handleSelectionChange = useCallback((keys: 'all' | Set<React.Key>): void => {
    if (keys === 'all') return;
    const selected = Array.from(keys)[0] as string | undefined;
    setSelectedKey(selected ?? null);
    // Reset duplicate confirm when selection changes
    setShowDuplicateConfirm(false);
    setSubmitError(null);
  }, []);

  /**
   * Perform the actual import
   */
  const performImport = useCallback(async (): Promise<void> => {
    if (!selectedCatalogEntry) return;

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Lookup fee details from catalog
      const feeDetails = getBrokerFeeDetails(selectedCatalogEntry.id);
      const lastUpdated = feeDetails?.lastUpdated ?? new Date().toISOString();

      // Transform fee configurations if available
      const transformedFeeConfigs: SchemaBrokerFeeConfiguration[] = feeDetails
        ? feeDetails.feeConfigurations.map((config) =>
            transformCatalogFeeConfiguration(config, lastUpdated)
          )
        : [];

      // Build broker data, conditionally including feeMetadata only if present
      const brokerData: Parameters<typeof brokerService.add>[0] = {
        name: selectedCatalogEntry.name,
        brokerReferenceId: selectedCatalogEntry.id,
        brokerReferenceUpdatedAt: lastUpdated,
        feeConfigurations: transformedFeeConfigs,
      };
      if (feeDetails?.feeMetadata) {
        brokerData.feeMetadata = feeDetails.feeMetadata;
      }

      const newBroker = await brokerService.add(brokerData);

      syncCoordinatorService.notifyChange('brokers', newBroker.id);

      showToast({
        title: 'Broker Imported',
        description: `"${selectedCatalogEntry.name}" has been added`,
        color: 'success',
      });

      onImportComplete(newBroker.id);
      onClose();
    } catch (error) {
      console.error('Failed to import broker:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to import broker';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedCatalogEntry, showToast, onImportComplete, onClose]);

  /**
   * Handle import button click
   */
  const handleImport = useCallback((): void => {
    if (!selectedCatalogEntry) return;

    if (duplicateExists) {
      // Show duplicate confirmation
      setShowDuplicateConfirm(true);
    } else {
      // No duplicate, import directly
      performImport();
    }
  }, [selectedCatalogEntry, duplicateExists, performImport]);

  /**
   * Handle "Use Existing" action
   */
  const handleUseExisting = useCallback((): void => {
    if (!mostRecentExisting) return;

    onImportComplete(mostRecentExisting.id);
    onClose();
  }, [mostRecentExisting, onImportComplete, onClose]);

  /**
   * Handle cancel
   */
  const handleCancel = useCallback((): void => {
    onClose();
  }, [onClose]);

  // ===========================================================================
  // Render Helpers
  // ===========================================================================

  const hasResults = filteredCatalog.length > 0;
  const hasActiveFilters =
    filters.searchTerm !== '' ||
    filters.accountTypeFilter !== 'all' ||
    filters.assetClassFilter !== 'all' ||
    filters.popularOnly;

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleCancel();
      }}
      size="2xl"
      classNames={{
        base: 'max-h-[100vh] sm:max-h-[90vh] h-full sm:h-auto',
        wrapper: 'items-end sm:items-center p-0 sm:p-6',
        backdrop: 'bg-overlay/50',
      }}
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1 pb-0 px-4 sm:px-6 pt-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">Import from Catalog</h3>
                </div>
                {selectedCatalogEntry && (
                  <Chip color="primary" size="sm" variant="flat">
                    1 selected
                  </Chip>
                )}
              </div>
              <p className="text-sm text-text-secondary font-normal">
                Select a broker to add to your portfolio
              </p>

              {/* Search and Filters Section */}
              {!loading && (
                <div className="space-y-4 pt-4 border-t border-default-200" ref={portalContainerRef}>
                  <Input
                    placeholder="Search brokers by name..."
                    value={filters.searchTerm}
                    onValueChange={(value) => updateFilter('searchTerm', value)}
                    startContent={<Search className="w-4 h-4 text-text-secondary" />}
                    className="w-full"
                    classNames={{ input: 'text-base' }}
                  />

                  {/* Desktop: Show filters inline */}
                  <div className="hidden sm:block">
                    <div className="grid grid-cols-3 gap-4 items-end">
                      <Select
                        label="Account Type"
                        variant="underlined"
                        size="sm"
                        selectedKeys={[filters.accountTypeFilter]}
                        onSelectionChange={(keys) => {
                          const selected = Array.from(keys)[0] as FilterState['accountTypeFilter'];
                          updateFilter('accountTypeFilter', selected);
                        }}
                        aria-label="Filter by account type"
                        {...(portalContainerRef.current && {
                          popoverProps: { portalContainer: portalContainerRef.current },
                        })}
                      >
                        {accountTypeFilterOptions.map((item) => (
                          <SelectItem key={item.key}>{item.label}</SelectItem>
                        ))}
                      </Select>

                      <Select
                        label="Asset Class"
                        variant="underlined"
                        size="sm"
                        selectedKeys={[filters.assetClassFilter]}
                        onSelectionChange={(keys) => {
                          const selected = Array.from(keys)[0] as FilterState['assetClassFilter'];
                          updateFilter('assetClassFilter', selected);
                        }}
                        aria-label="Filter by asset class"
                        {...(portalContainerRef.current && {
                          popoverProps: { portalContainer: portalContainerRef.current },
                        })}
                      >
                        {assetClassFilterOptions.map((item) => (
                          <SelectItem key={item.key}>{item.label}</SelectItem>
                        ))}
                      </Select>

                      <div className="flex items-center gap-2 pb-1">
                        <Switch
                          size="sm"
                          isSelected={filters.popularOnly}
                          onValueChange={(value) => updateFilter('popularOnly', value)}
                        />
                        <span className="text-sm text-text-secondary">Popular only</span>
                      </div>
                    </div>
                  </div>

                  {/* Mobile: Collapsible filters in Accordion */}
                  <div className="sm:hidden">
                    <Accordion variant="light" isCompact>
                      <AccordionItem
                        key="filters"
                        aria-label="Filter options"
                        title={`Filters ${hasActiveFilters ? '(Active)' : ''}`}
                        classNames={{
                          title: 'text-sm font-medium',
                          trigger: 'py-2',
                          content: 'pt-0 pb-2',
                        }}
                      >
                        <div className="space-y-3">
                          <Select
                            label="Account Type"
                            variant="bordered"
                            size="sm"
                            selectedKeys={[filters.accountTypeFilter]}
                            onSelectionChange={(keys) => {
                              const selected = Array.from(keys)[0] as FilterState['accountTypeFilter'];
                              updateFilter('accountTypeFilter', selected);
                            }}
                            aria-label="Filter by account type"
                            {...(portalContainerRef.current && {
                              popoverProps: { portalContainer: portalContainerRef.current },
                            })}
                          >
                            {accountTypeFilterOptions.map((item) => (
                              <SelectItem key={item.key}>{item.label}</SelectItem>
                            ))}
                          </Select>

                          <Select
                            label="Asset Class"
                            variant="bordered"
                            size="sm"
                            selectedKeys={[filters.assetClassFilter]}
                            onSelectionChange={(keys) => {
                              const selected = Array.from(keys)[0] as FilterState['assetClassFilter'];
                              updateFilter('assetClassFilter', selected);
                            }}
                            aria-label="Filter by asset class"
                            {...(portalContainerRef.current && {
                              popoverProps: { portalContainer: portalContainerRef.current },
                            })}
                          >
                            {assetClassFilterOptions.map((item) => (
                              <SelectItem key={item.key}>{item.label}</SelectItem>
                            ))}
                          </Select>

                          <div className="flex items-center gap-2">
                            <Switch
                              size="sm"
                              isSelected={filters.popularOnly}
                              onValueChange={(value) => updateFilter('popularOnly', value)}
                            />
                            <span className="text-sm text-text-secondary">Popular only</span>
                          </div>
                        </div>
                      </AccordionItem>
                    </Accordion>
                  </div>

                  {/* Results count */}
                  <p className="text-sm text-text-secondary">
                    {filteredCatalog.length} broker{filteredCatalog.length !== 1 ? 's' : ''} found
                  </p>
                </div>
              )}
            </ModalHeader>

            <ModalBody className="pt-4 px-4 sm:px-6 pb-4 sm:pb-6 flex-1 min-h-0">
              {/* Loading state */}
              {loading && (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-default-300 mx-auto mb-4 animate-pulse" />
                  <h4 className="text-large font-medium text-default-500 mb-2">Loading</h4>
                  <p className="text-small text-default-400">Please wait...</p>
                </div>
              )}

              {/* Duplicate confirmation */}
              {!loading && showDuplicateConfirm && selectedCatalogEntry && (
                <div className="rounded-lg bg-warning-50 border border-warning-200 p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-warning-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-warning-800 mb-1">
                        Broker Already Imported
                      </p>
                      <p className="text-sm text-warning-700 mb-3">
                        You already have a broker from &quot;{selectedCatalogEntry.name}&quot; in your
                        portfolio. You can use the existing one or import another instance.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          size="sm"
                          variant="flat"
                          onPress={handleUseExisting}
                          isDisabled={isSubmitting}
                        >
                          Use Existing
                        </Button>
                        <Button
                          size="sm"
                          color="warning"
                          onPress={performImport}
                          isLoading={isSubmitting}
                        >
                          Import Anyway
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error alert */}
              {submitError && (
                <Alert color="danger" variant="flat" className="mb-4">
                  {submitError}
                </Alert>
              )}

              {/* Broker list */}
              {!loading && (
                <div className="flex-1 min-h-0">
                  {hasResults ? (
                    <Listbox
                      aria-label="Select broker"
                      selectionMode="single"
                      selectedKeys={selectedKey ? new Set([selectedKey]) : new Set()}
                      onSelectionChange={handleSelectionChange}
                      className="w-full"
                    >
                      {[
                        // Personal/General brokers section
                        ...(groupedCatalog.personal.length > 0
                          ? [
                              <ListboxSection
                                key="personal"
                                title="Personal / General"
                                showDivider={
                                  groupedCatalog.prop.length > 0 || groupedCatalog.paper.length > 0
                                }
                                classNames={{
                                  heading: 'text-tiny font-semibold text-default-500 uppercase tracking-wide',
                                  group: 'divide-y divide-default-100',
                                }}
                              >
                                {groupedCatalog.personal.map((entry) => {
                                  const isImported = existingBrokerReferenceIds.has(entry.id);
                                  return (
                                    <ListboxItem
                                      key={entry.id}
                                      className="py-3"
                                      textValue={entry.name}
                                    >
                                      <div className="flex justify-between items-start w-full">
                                        <div className="flex flex-col flex-1 min-w-0">
                                          <span className="text-small font-semibold">
                                            {entry.name}
                                          </span>
                                          {entry.shortName && entry.shortName !== entry.name && (
                                            <span className="text-tiny text-default-400">
                                              {entry.shortName}
                                            </span>
                                          )}
                                          {isImported && (
                                            <span className="text-tiny text-warning-500 font-medium mt-1">
                                              Already imported
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap gap-1 ml-2">
                                          {entry.isPopular && (
                                            <Chip size="sm" variant="flat" color="success">
                                              Popular
                                            </Chip>
                                          )}
                                        </div>
                                      </div>
                                    </ListboxItem>
                                  );
                                })}
                              </ListboxSection>,
                            ]
                          : []),
                        // Prop firm brokers section
                        ...(groupedCatalog.prop.length > 0
                          ? [
                              <ListboxSection
                                key="prop"
                                title="Prop Firms"
                                showDivider={groupedCatalog.paper.length > 0}
                                classNames={{
                                  heading: 'text-tiny font-semibold text-default-500 uppercase tracking-wide',
                                  group: 'divide-y divide-default-100',
                                }}
                              >
                                {groupedCatalog.prop.map((entry) => {
                                  const isImported = existingBrokerReferenceIds.has(entry.id);
                                  return (
                                    <ListboxItem
                                      key={entry.id}
                                      className="py-3"
                                      textValue={entry.name}
                                    >
                                      <div className="flex justify-between items-start w-full">
                                        <div className="flex flex-col flex-1 min-w-0">
                                          <span className="text-small font-semibold">
                                            {entry.name}
                                          </span>
                                          {entry.shortName && entry.shortName !== entry.name && (
                                            <span className="text-tiny text-default-400">
                                              {entry.shortName}
                                            </span>
                                          )}
                                          {isImported && (
                                            <span className="text-tiny text-warning-500 font-medium mt-1">
                                              Already imported
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap gap-1 ml-2">
                                          <Chip size="sm" variant="flat" color="warning">
                                            Prop
                                          </Chip>
                                          {entry.isPopular && (
                                            <Chip size="sm" variant="flat" color="success">
                                              Popular
                                            </Chip>
                                          )}
                                        </div>
                                      </div>
                                    </ListboxItem>
                                  );
                                })}
                              </ListboxSection>,
                            ]
                          : []),
                        // Paper trading brokers section
                        ...(groupedCatalog.paper.length > 0
                          ? [
                              <ListboxSection
                                key="paper"
                                title="Paper Trading"
                                classNames={{
                                  heading: 'text-tiny font-semibold text-default-500 uppercase tracking-wide',
                                  group: 'divide-y divide-default-100',
                                }}
                              >
                                {groupedCatalog.paper.map((entry) => {
                                  const isImported = existingBrokerReferenceIds.has(entry.id);
                                  return (
                                    <ListboxItem
                                      key={entry.id}
                                      className="py-3"
                                      textValue={entry.name}
                                    >
                                      <div className="flex justify-between items-start w-full">
                                        <div className="flex flex-col flex-1 min-w-0">
                                          <span className="text-small font-semibold">
                                            {entry.name}
                                          </span>
                                          {entry.shortName && entry.shortName !== entry.name && (
                                            <span className="text-tiny text-default-400">
                                              {entry.shortName}
                                            </span>
                                          )}
                                          {isImported && (
                                            <span className="text-tiny text-warning-500 font-medium mt-1">
                                              Already imported
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap gap-1 ml-2">
                                          <Chip size="sm" variant="flat" color="secondary">
                                            Paper
                                          </Chip>
                                          {entry.isPopular && (
                                            <Chip size="sm" variant="flat" color="success">
                                              Popular
                                            </Chip>
                                          )}
                                        </div>
                                      </div>
                                    </ListboxItem>
                                  );
                                })}
                              </ListboxSection>,
                            ]
                          : []),
                      ]}
                    </Listbox>
                  ) : (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 text-default-300 mx-auto mb-4" />
                      <h4 className="text-large font-medium text-default-500 mb-2">
                        No Brokers Found
                      </h4>
                      <p className="text-small text-default-400">
                        Try adjusting your search terms or filters
                      </p>
                    </div>
                  )}
                </div>
              )}
            </ModalBody>

            <ModalFooter className="px-4 sm:px-6 py-4 sm:py-6">
              <Button color="danger" variant="light" onPress={handleCancel} isDisabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                color="primary"
                onPress={handleImport}
                isDisabled={!selectedKey || isSubmitting || showDuplicateConfirm}
                isLoading={isSubmitting && !showDuplicateConfirm}
              >
                Import
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default BrokerImportModal;
